import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Row, Col, Space, Tooltip, message, Input } from 'antd';
import { PlusOutlined, MinusOutlined, ReloadOutlined, DeleteOutlined, PlusCircleOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { collection, doc, setDoc, getDocs, deleteDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../apps/prayer-counter/firebase';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Text } = Typography;

const CounterMain = () => {
  const [counters, setCounters] = useState([
    { id: 1, value: 0, label: 'Counter 1' }
  ]);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(true);
  const { getUserId } = useAuth();

  // Load counters from Firestore on mount
  useEffect(() => {
    const loadCounters = async () => {
      const userId = getUserId();
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        console.log('📊 Loading counters from Firestore for user:', userId);
        
        // Set up real-time listener
        const unsubscribe = onSnapshot(
          collection(db, `users/${userId}/randomCounters`),
          async (querySnapshot) => {
            const counterList = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));

            if (counterList.length > 0) {
              // Sort by creation order
              counterList.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.createdAt?.toDate?.() || new Date(0);
                return dateA - dateB;
              });
              setCounters(counterList);
              console.log(`✅ Loaded ${counterList.length} counters from Firestore`);
            } else {
              // Create default counter if none exist
              const defaultCounter = { id: '1', value: 0, label: 'Counter 1' };
              setCounters([defaultCounter]);
              await saveCounter(defaultCounter, userId);
            }
            setLoading(false);
          },
          (error) => {
            console.error('❌ Error loading counters:', error);
            // Fallback to localStorage
            loadFromLocalStorage();
            setLoading(false);
          }
        );

        return unsubscribe;
      } catch (error) {
        console.error('❌ Error setting up Firestore listener:', error);
        loadFromLocalStorage();
        setLoading(false);
      }
    };

    loadCounters();
  }, [getUserId]);

  // Fallback to localStorage
  const loadFromLocalStorage = () => {
    const savedCounters = localStorage.getItem('randomCounters');
    if (savedCounters) {
      try {
        const parsed = JSON.parse(savedCounters);
        setCounters(parsed);
      } catch (error) {
        console.error('Failed to load counters from localStorage:', error);
      }
    }
  };

  // Save counter to Firestore
  const saveCounter = async (counter, userId = null) => {
    const uid = userId || getUserId();
    if (!uid) {
      // Fallback to localStorage if not authenticated
      const currentCounters = JSON.parse(localStorage.getItem('randomCounters') || '[]');
      const updatedCounters = currentCounters.filter(c => c.id !== counter.id);
      updatedCounters.push(counter);
      localStorage.setItem('randomCounters', JSON.stringify(updatedCounters));
      return;
    }

    try {
      await setDoc(doc(db, `users/${uid}/randomCounters`, counter.id.toString()), {
        ...counter,
        lastUpdated: serverTimestamp(),
        createdAt: counter.createdAt || serverTimestamp()
      });
    } catch (error) {
      console.error('❌ Error saving counter:', error);
      message.error('Failed to save counter');
    }
  };

  // Delete counter from Firestore
  const deleteCounter = async (counterId) => {
    const userId = getUserId();
    if (!userId) return;

    try {
      await deleteDoc(doc(db, `users/${userId}/randomCounters`, counterId.toString()));
    } catch (error) {
      console.error('❌ Error deleting counter:', error);
      message.error('Failed to delete counter');
    }
  };

  const updateCounter = async (id, delta) => {
    const counter = counters.find(c => c.id === id);
    if (!counter) return;

    const newValue = Math.max(0, counter.value + delta);
    const updatedCounter = { ...counter, value: newValue };
    
    // Update local state immediately for better UX
    setCounters(prev => prev.map(c => 
      c.id === id ? updatedCounter : c
    ));

    // Save to Firestore
    await saveCounter(updatedCounter);

    // Haptic feedback for mobile
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  };

  const resetCounter = async (id) => {
    const counter = counters.find(c => c.id === id);
    if (!counter) return;

    const updatedCounter = { ...counter, value: 0 };
    
    // Update local state
    setCounters(prev => prev.map(c => 
      c.id === id ? updatedCounter : c
    ));

    // Save to Firestore
    await saveCounter(updatedCounter);
    message.success('Counter reset! 🔄');
  };

  const addCounter = async () => {
    const newId = Math.max(...counters.map(c => parseInt(c.id) || 0)) + 1;
    const newCounter = { 
      id: newId.toString(), 
      value: 0, 
      label: `Counter ${newId}`,
      createdAt: new Date()
    };
    
    // Update local state
    setCounters(prev => [...prev, newCounter]);
    
    // Save to Firestore
    await saveCounter(newCounter);
    message.success('New counter added! ➕');
  };

  const removeCounter = async (id) => {
    if (counters.length <= 1) {
      message.warning('At least one counter is required');
      return;
    }
    
    // Update local state
    setCounters(prev => prev.filter(counter => counter.id !== id));
    
    // Delete from Firestore
    await deleteCounter(id);
    message.success('Counter removed! 🗑️');
  };

  const resetAllCounters = async () => {
    const updatedCounters = counters.map(counter => ({ ...counter, value: 0 }));
    
    // Update local state
    setCounters(updatedCounters);
    
    // Save all to Firestore
    for (const counter of updatedCounters) {
      await saveCounter(counter);
    }
    message.success('All counters reset! 🔄');
  };

  const getTotalCount = () => {
    return counters.reduce((sum, counter) => sum + counter.value, 0);
  };

  const getCounterColor = (value) => {
    if (value === 0) return '#ff7875';
    if (value < 10) return '#FFB347';
    if (value < 50) return '#FF8C00';
    if (value < 100) return '#faad14';
    return '#52c41a';
  };

  // Name editing functions
  const startEditing = (counter) => {
    setEditingId(counter.id);
    setEditingName(counter.label);
  };

  const saveNameEdit = async () => {
    if (!editingName.trim()) {
      message.warning('Counter name cannot be empty');
      return;
    }

    const counter = counters.find(c => c.id === editingId);
    if (!counter) return;

    const updatedCounter = { ...counter, label: editingName.trim() };
    
    // Update local state
    setCounters(prev => prev.map(c => 
      c.id === editingId ? updatedCounter : c
    ));

    // Save to Firestore
    await saveCounter(updatedCounter);
    
    setEditingId(null);
    setEditingName('');
    message.success('Counter name updated! ✏️');
  };

  const cancelNameEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const CounterCard = ({ counter }) => (
    <Col xs={24} sm={12} lg={8} xl={6} key={counter.id}>
      <Card
        style={{
          borderRadius: '20px',
          border: `3px solid ${getCounterColor(counter.value)}`,
          boxShadow: `0 8px 24px ${getCounterColor(counter.value)}20`,
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF8F0 100%)',
          textAlign: 'center',
          overflow: 'hidden'
        }}
        styles={{ body: { padding: '24px 16px' } }}
      >
        {/* Counter Label */}
        <div style={{ marginBottom: '16px' }}>
          {editingId === counter.id ? (
            // Editing mode
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onPressEnter={saveNameEdit}
                style={{ 
                  textAlign: 'center',
                  fontSize: '14px',
                  maxWidth: '120px'
                }}
                autoFocus
              />
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={saveNameEdit}
                style={{ color: '#52c41a' }}
                size="small"
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={cancelNameEdit}
                style={{ color: '#ff4d4f' }}
                size="small"
              />
            </div>
          ) : (
            // Display mode
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              <Text 
                style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: '500',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'all 0.2s'
                }}
                onClick={() => startEditing(counter)}
                title="Click to edit name"
              >
                {counter.label}
              </Text>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => startEditing(counter)}
                style={{ 
                  color: '#666',
                  fontSize: '10px'
                }}
                size="small"
              />
            </div>
          )}
          
          {counters.length > 1 && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeCounter(counter.id)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                fontSize: '12px'
              }}
              size="small"
            />
          )}
        </div>

        {/* Counter Display */}
        <div style={{
          background: `linear-gradient(135deg, ${getCounterColor(counter.value)} 0%, ${getCounterColor(counter.value)}CC 100%)`,
          borderRadius: '50%',
          width: '120px',
          height: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: `0 4px 16px ${getCounterColor(counter.value)}40`
        }}>
          <Text style={{
            fontSize: 'clamp(24px, 6vw, 36px)',
            fontWeight: 'bold',
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            {counter.value}
          </Text>
        </div>

        {/* Counter Controls */}
        <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
          <Button
            icon={<MinusOutlined />}
            onClick={() => updateCounter(counter.id, -1)}
            disabled={counter.value <= 0}
            style={{
              borderColor: '#FF8C00',
              color: '#FF8C00',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            size="large"
          />
          
          <Tooltip title="Reset this counter">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => resetCounter(counter.id)}
              style={{
                borderColor: '#faad14',
                color: '#faad14',
                width: '40px',
                height: '40px',
                borderRadius: '50%'
              }}
            />
          </Tooltip>
          
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => updateCounter(counter.id, 1)}
            style={{
              background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
              borderColor: '#FF8C00',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            size="large"
          />
        </Space>
      </Card>
    </Col>
  );

  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto',
      padding: '0'
    }}>
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '400px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔢</div>
            <Text style={{ color: '#FF8C00', fontSize: '16px' }}>Loading your counters...</Text>
          </div>
        </div>
      ) : (
        <>
          {/* Header Stats */}
          <Card
            style={{ 
              marginBottom: 16,
              background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE4B5 100%)',
              border: '1px solid #FFB347',
              borderRadius: '16px'
            }}
            styles={{ body: { padding: '16px 20px' } }}
          >
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <div>
                  <Title level={3} style={{ 
                    color: '#FF8C00', 
                    margin: 0,
                    fontSize: 'clamp(20px, 5vw, 28px)'
                  }}>
                    🔢 Random Counters
                  </Title>
                  <Text style={{ color: '#FF7F00', fontSize: '14px' }}>
                    Active Counters: {counters.length} • Total Count: {getTotalCount()}
                  </Text>
                </div>
              </Col>
              <Col>
                <Space>
                  <Tooltip title="Add new counter">
                    <Button
                      icon={<PlusCircleOutlined />}
                      onClick={addCounter}
                      style={{
                        borderColor: '#52c41a',
                        color: '#52c41a'
                      }}
                    >
                      Add Counter
                    </Button>
                  </Tooltip>
                  <Tooltip title="Reset all counters">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={resetAllCounters}
                      danger
                      disabled={getTotalCount() === 0}
                    >
                      Reset All
                    </Button>
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Counters Grid */}
          <Row gutter={[16, 16]}>
            {counters.map(counter => (
              <CounterCard key={counter.id} counter={counter} />
            ))}
          </Row>

          {/* Empty State */}
          {counters.length === 0 && (
            <Card style={{ 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 100%)',
              border: '2px dashed #FFB347',
              borderRadius: '20px',
              margin: '40px 0'
            }}>
              <div style={{ padding: '60px 20px' }}>
                <div style={{ fontSize: '72px', marginBottom: '16px' }}>
                  🔢
                </div>
                <Title level={3} style={{ color: '#FF8C00', marginBottom: '8px' }}>
                  No Counters Yet
                </Title>
                <Text style={{ color: '#666', fontSize: '16px', marginBottom: '24px', display: 'block' }}>
                  Add your first counter to start counting anything!
                </Text>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusCircleOutlined />}
                  onClick={addCounter}
                  style={{
                    background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                    borderColor: '#FF8C00',
                    height: '48px',
                    fontSize: '16px',
                    padding: '0 32px'
                  }}
                >
                  Create First Counter
                </Button>
              </div>
            </Card>
          )}

          {/* Tips */}
          <Card
            style={{ 
              marginTop: 24,
              background: 'rgba(255, 255, 255, 0.6)',
              border: '1px solid #FFE4B5',
              borderRadius: '12px'
            }}
            styles={{ body: { padding: '16px' } }}
          >
            <Text style={{ color: '#666', fontSize: '12px' }}>
              💡 <strong>Tips:</strong> 
              • Click counter names to edit them
              • Tap + to increment, - to decrement 
              • Reset individual counters or all at once 
              • Add multiple counters for different things 
              • Your counts are automatically saved to the cloud! ☁️
            </Text>
          </Card>
        </>
      )}
    </div>
  );
};

export default CounterMain; 